import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ChatStateService } from '@lib/chat/services/chat-state.service';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { GuaranteeApplication } from '@lib/core/models/guarantee-application.interface';
import { MemberProfile } from '@lib/core/models/member.interface';
import { PrescriptionHistoryStatus, PrescriptionStatus, PrescriptionTypes } from '@lib/core/models/prescription.interface';

import { DiagnosisComponent } from '@lib/shared-ui/commons/diagnosis/diagnosis.component';
import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';

@Component({
  selector: 'chat-guarantees-list',
  standalone: true,
  imports: [MatFormFieldModule, MatIcon, ReactiveFormsModule, DiagnosisComponent, TranslatePipe, MatTooltip, StatusComponent, NgClass],
  templateUrl: './chat-guarantees-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatGuaranteesListComponent {
  private readonly router = inject(Router);
  private readonly chatState = inject(ChatStateService);

  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly formatDate = formatDate;

  member = input.required<MemberProfile>();
  searchOutput = output<string>();

  protected readonly searchFc: FormControl<string> = new FormControl<string>(null);
  protected readonly searchTerm = signal<string>('');

  // Use data from ChatStateService instead of making API call
  protected readonly filteredApplications = computed<GuaranteeApplication[]>(() => {
    const applications = this.chatState.guarantees();
    const term = this.searchTerm().trim().toLowerCase();

    if (!term) return applications;

    return applications.filter(
      app =>
        app.diagnoses?.some(diagnosis => diagnosis.icd10.name?.toLowerCase().includes(term) || diagnosis.icpc2.code?.toLowerCase().includes(term)) ||
        app.prescriptions?.some(prescription => prescription.name?.toLowerCase().includes(term))
    );
  });

  protected navigateToMedicalServiceCreate() {
    this.router.navigate([`members/${this.member().id}/guarantees/create`]).then();
  }

  protected navigateToMedicalServiceDetails(guaranteeId: string) {
    this.router.navigate([`members/${this.member().id}/guarantees/${guaranteeId}/edit`]).then();
  }

  protected filterPrescriptions(guarantee: GuaranteeApplication) {
    return guarantee.prescriptions.filter(item => item.status !== PrescriptionStatus.Archived || item.historyStatus !== PrescriptionHistoryStatus.Archived);
  }
}
